/**
 * Token Status Indicator
 * Shows Withings token health and expiration countdown
 */

import type { TokenHealthStatus } from "../../services/tokenStatusService";

import React, { useState, useEffect } from "react";

import { TokenStatusService } from "../../services/tokenStatusService";
import "./TokenStatusIndicator.css";

interface TokenStatusIndicatorProperties {
  /** Auto-refresh interval in seconds (0 = disabled) */
  autoRefresh?: number;
  /** Custom CSS classes */
  className?: string;
  /** Show detailed information */
  showDetails?: boolean;
  /** Size variant */
  size?: "lg" | "md" | "sm";
}

/**
 * Token Status Indicator Component
 *
 * @param root0
 *
 * @param root0.showDetails
 *
 * @param root0.size
 *
 * @param root0.className
 *
 * @param root0.autoRefresh
 *
 * @returns {JSX.Element} Token status indicator component
 *
 */
export const TokenStatusIndicator: React.FC<TokenStatusIndicatorProperties> = ({
  autoRefresh = 30,
  className = "",
  showDetails = false,
  size = "md",
}) => {
  const [healthStatus, setHealthStatus] = useState<null | TokenHealthStatus>(null);
  const [isLoading, setIsLoading] = useState(true);

  // Load initial token health
  useEffect(() => {
    void loadTokenHealth();
  }, []);

  // Set up auto-refresh if enabled
  useEffect(() => {
    if (autoRefresh <= 0) {
      return;
    }

    const interval = setInterval(() => {
      void loadTokenHealth();
    }, autoRefresh * 1000);
    return () => {
      clearInterval(interval);
    };
  }, [autoRefresh]);

  const loadTokenHealth = async () => {
    try {
      setIsLoading(true);
      const health = await TokenStatusService.getTokenHealth();
      setHealthStatus(health);
    } catch {
      // NOTE: Failed to load token health - silently ignore
    } finally {
      setIsLoading(false);
    }
  };

  if (isLoading) {
    return (
      <div className={`token-status-indicator ${size} ${className} loading`}>
        <div className="status-dot loading" />
        <span>Lade Status...</span>
      </div>
    );
  }

  if (!healthStatus) {
    return (
      <div className={`token-status-indicator ${size} ${className}`}>
        <div className="status-dot unknown" />
        <span>Token Status unbekannt</span>
      </div>
    );
  }

  const { expiresAt, formattedTimeLeft, statusColor, userId } = healthStatus;

  return (
    <div className={`token-status-indicator ${size} ${className}`}>
      <div className={`status-dot ${statusColor}`} />

      <div className="status-content">
        <span className="status-text">
          {statusColor === "expired" && "Token abgelaufen"}
          {statusColor === "critical" && "Token läuft bald ab!"}
          {statusColor === "warning" && "Token läuft bald ab"}
          {statusColor === "healthy" && "Token gültig"}
        </span>

        {showDetails ? (
          <div className="token-details">
            <div className="time-remaining">
              Verbleibend: <strong>{formattedTimeLeft}</strong>
            </div>
            <div className="expires-at">
              Läuft ab: {new Date(expiresAt).toLocaleString("de-DE")}
            </div>
            <div className="user-id">User ID: {userId}</div>
          </div>
        ) : (
          <span className="time-remaining-compact">{formattedTimeLeft}</span>
        )}
      </div>
    </div>
  );
};
